import { useAuth } from "@/_core/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { BRAND_SLOGAN, consumePostLoginRedirect, getLoginUrl } from "@/const";
import { startNewGenerationSession } from "@/lib/generationSession";
import { ArrowRight, CheckCircle2, Image, Paintbrush, Sparkles, Wand2 } from "lucide-react";
import { SiteHeader, SiteFooter } from "@/components/SiteHeader";
import { useEffect } from "react";
import { useLocation } from "wouter";

/* ============================================================
 * V1 products — customer-facing "작품" choices, not technical modes.
 * Prices shown here are marketing copy for the landing page; the actual
 * order flow (/order) still drives its own pricing from the products
 * table, unchanged in this step.
 * ============================================================ */
const FRAME_PRICING = [
  { size: "A4", price: "15,000원" },
  { size: "A3", price: "20,000원" },
  { size: "A2", price: "30,000원" },
];

const PRODUCTS = [
  {
    key: "photo",
    icon: Image,
    accent: "text-blue-600",
    title: "사진 그대로 작품으로",
    flagship: false,
    description: "소중한 사진의 분위기와 구도를 그대로 살려 캔버스 작품으로 완성해드려요.",
    button: "그대로 만들기",
    // No AI transform — straight to the print-order flow.
    path: "/order",
    prices: [
      { size: "A4", price: "29,000원", highlight: false },
      { size: "A3", price: "45,000원", highlight: false },
      { size: "A2", price: "69,000원", highlight: false },
    ],
  },
  {
    key: "oil",
    icon: Paintbrush,
    accent: "text-amber-600",
    title: "내 사진을 유화 작품으로",
    flagship: true,
    description: "얼굴·표정·포즈는 살리고 붓터치와 질감을 더해 한 점의 유화 작품으로 만들어드려요.",
    button: "유화로 만들기",
    // AI 유화 변환 화면 — entryMode="oil" is UX framing only, see Create.tsx.
    path: "/create?mode=oil",
    prices: [
      { size: "A4", price: "49,000원", highlight: false },
      { size: "A3", price: "69,000원", highlight: true },
      { size: "A2", price: "99,000원", highlight: false },
    ],
  },
  {
    key: "custom",
    icon: Wand2,
    accent: "text-rose-600",
    title: "원하는 모습으로 맞춤 작품",
    flagship: false,
    description: "배경 변경, 색상 수정, 분위기 변화 등 원하는 내용을 반영해 나만의 작품으로 만들어드려요.",
    button: "맞춤으로 만들기",
    // AI 맞춤 편집 화면 — entryMode="custom" is UX framing only, see Create.tsx.
    path: "/create?mode=custom",
    prices: [
      { size: "A4", price: "59,000원", highlight: false },
      { size: "A3", price: "89,000원", highlight: false },
      { size: "A2", price: "129,000원", highlight: false },
    ],
  },
] as const;

const FLAGSHIP = PRODUCTS.find((p) => p.flagship)!;
const SECONDARY = PRODUCTS.filter((p) => !p.flagship);

export default function Home() {
  const { isAuthenticated } = useAuth();
  const [, navigate] = useLocation();
  const [sloganLine1, sloganLine2] = BRAND_SLOGAN.split(", ");

  // The Google OAuth callback always lands on "/" (googleOAuth.ts is
  // unchanged). If the visitor was mid-click on a product card before being
  // sent to login, getLoginUrl() remembered that destination — forward them
  // there now instead of leaving them stranded on the homepage.
  useEffect(() => {
    if (!isAuthenticated) return;
    const redirect = consumePostLoginRedirect();
    if (redirect) navigate(redirect);
  }, [isAuthenticated, navigate]);

  // wouter's navigate() only handles internal SPA routes — for a logged-out
  // visitor, the Google login URL is an absolute external URL, which
  // navigate() silently no-ops on (confirmed: clicking did nothing). Route
  // external targets through a real browser navigation instead. Each
  // product now goes to its own destination (see PRODUCTS' `path` field)
  // instead of everything sharing one order-flow link; getLoginUrl's
  // returnPath carries the ORIGINAL destination through Google login so a
  // logged-out visitor lands back on the product they picked, not just "/".
  const goTo = (path: string) => {
    // Entering the AI create flow from Home always starts a NEW artwork
    // (new generation session). Reloading /create or coming back to it from
    // Options keeps the current session — only this entry replaces it.
    if (path.startsWith("/create")) startNewGenerationSession();
    const target = isAuthenticated ? path : getLoginUrl(path);
    if (target.startsWith("http")) {
      window.location.href = target;
    } else {
      navigate(target);
    }
  };
  const goToOrder = () => goTo("/order");

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <SiteHeader />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-amber-50/80 via-background to-stone-100/50" />
        <div className="container relative py-16 md:py-28 lg:py-36">
          <div className="max-w-2xl mx-auto text-center space-y-6 md:space-y-8">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 text-primary text-xs font-medium">
              <Sparkles className="w-3.5 h-3.5" />
              {BRAND_SLOGAN}
            </div>
            <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl text-primary leading-tight">
              {sloganLine1},<br />
              <span className="text-amber-700">{sloganLine2}</span>
            </h1>
            <p className="text-base md:text-lg text-muted-foreground max-w-lg mx-auto leading-relaxed">
              아이 사진과 반려동물 사진을 <br className="sm:hidden" />
              나만의 작품으로 완성해드립니다.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center pt-2">
              <Button size="lg" className="text-base px-8 rounded-full gap-2" onClick={() => goTo(FLAGSHIP.path)}>
                유화로 만들기
                <Paintbrush className="w-4 h-4" />
              </Button>
              <Button size="lg" variant="outline" className="rounded-full text-base px-8 gap-2" onClick={goToOrder}>
                그대로 만들기
                <ArrowRight className="w-4 h-4" />
              </Button>
            </div>
            <div className="flex items-center justify-center gap-4 md:gap-6 pt-2 text-sm text-muted-foreground">
              <span className="flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-green-600" />프리미엄 인화</span>
              <span className="flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-green-600" />3~5일 제작</span>
              <span className="flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-green-600" />만족 보장</span>
            </div>
          </div>
        </div>
      </section>

      {/* Products (V1) */}
      <section className="py-16 md:py-24 bg-background">
        <div className="container max-w-5xl mx-auto px-4">
          <h2 className="font-serif text-2xl md:text-3xl text-center mb-3 text-primary">작품을 선택해주세요</h2>
          <p className="text-center text-sm text-muted-foreground mb-12">
            어떤 기술을 쓰는지가 아니라, 어떤 작품이 완성되는지로 골라주세요.
          </p>

          {/* Flagship product */}
          <div className="relative p-7 md:p-10 rounded-2xl border border-amber-400/60 bg-gradient-to-br from-amber-50/70 via-background to-background mb-8">
            <span className="absolute -top-3 left-6 md:left-8 inline-flex items-center gap-1 px-3 py-1 rounded-full bg-amber-600 text-white text-xs font-semibold">
              <Sparkles className="w-3 h-3" /> 대표상품
            </span>
            <div className="grid md:grid-cols-[auto_1fr_auto] gap-6 md:gap-8 items-center">
              <Paintbrush className="w-12 h-12 text-amber-600 mx-auto md:mx-0 shrink-0" />
              <div className="text-center md:text-left">
                <h3 className="font-serif text-2xl font-semibold mb-2">{FLAGSHIP.title}</h3>
                <p className="text-sm md:text-base text-muted-foreground mb-4 md:mb-0">{FLAGSHIP.description}</p>
              </div>
              <div className="flex flex-col items-center gap-3 md:min-w-[220px]">
                <div className="flex gap-3 text-sm">
                  {FLAGSHIP.prices.map((p) => (
                    <div
                      key={p.size}
                      className={`text-center px-2.5 py-1.5 rounded-lg ${p.highlight ? "bg-amber-600 text-white" : "text-foreground"}`}
                    >
                      <div className="text-xs opacity-80">{p.size}</div>
                      <div className="font-serif tabular-nums font-medium">{p.price}</div>
                    </div>
                  ))}
                </div>
                <Button
                  size="lg"
                  className="w-full rounded-full text-base gap-2"
                  onClick={() => goTo(FLAGSHIP.path)}
                >
                  {FLAGSHIP.button}
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>

          {/* Secondary products */}
          <div className="grid md:grid-cols-2 gap-6">
            {SECONDARY.map((product) => {
              const Icon = product.icon;
              return (
                <div key={product.key} className="p-6 md:p-8 rounded-2xl border border-border/60 bg-[#faf8f5] text-center flex flex-col">
                  <Icon className={`w-9 h-9 mx-auto mb-4 ${product.accent}`} />
                  <h3 className="font-serif text-lg font-semibold mb-2">{product.title}</h3>
                  <p className="text-sm text-muted-foreground mb-5 flex-1">{product.description}</p>
                  <div className="flex justify-center gap-4 text-sm mb-5">
                    {product.prices.map((p) => (
                      <div key={p.size} className="text-center">
                        <div className="text-xs text-muted-foreground">{p.size}</div>
                        <div className="font-serif tabular-nums font-medium">{p.price}</div>
                      </div>
                    ))}
                  </div>
                  <Button variant="outline" className="rounded-full gap-2" onClick={() => goTo(product.path)}>
                    {product.button}
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                </div>
              );
            })}
          </div>

          {/* Frame add-on (informational) */}
          <div className="mt-8 p-4 md:p-5 rounded-xl bg-muted/40 border border-border/50 text-center">
            <p className="text-sm font-medium text-foreground mb-2">프리미엄 액자 추가</p>
            <div className="flex justify-center gap-5 text-xs text-muted-foreground tabular-nums">
              {FRAME_PRICING.map((f) => (
                <span key={f.size}>{f.size} +{f.price}</span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-16 md:py-24 bg-muted/30">
        <div className="container max-w-4xl mx-auto px-4 text-center">
          <h2 className="font-serif text-2xl md:text-3xl mb-12 text-primary">간단한 주문 과정</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { step: "1", title: "사진 업로드" },
              { step: "2", title: "출력 방식 선택" },
              { step: "3", title: "사이즈 선택" },
              { step: "4", title: "결제" },
            ].map((item) => (
              <div key={item.step} className="flex flex-col items-center">
                <div className="w-10 h-10 rounded-full bg-primary/10 text-primary flex items-center justify-center font-bold text-lg mb-3">
                  {item.step}
                </div>
                <span className="text-sm font-medium">{item.title}</span>
              </div>
            ))}
          </div>
          <Button size="lg" className="mt-10 gap-2 rounded-full px-8" onClick={goToOrder}>
            지금 시작하기 <ArrowRight className="w-4 h-4" />
          </Button>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-primary/5 via-amber-50/50 to-stone-100/50">
        <div className="container text-center">
          <h2 className="font-serif text-3xl md:text-4xl text-primary mb-4">
            오늘의 사진을 내일의 작품으로.
          </h2>
          <p className="text-muted-foreground mb-8 max-w-md mx-auto">
            반려동물, 아이, 가족 사진 — 무엇이든 프리미엄 인화로 작품이 됩니다.
          </p>
          <Button size="lg" className="rounded-full px-10 text-base gap-2" onClick={goToOrder}>
            주문하기 <ArrowRight className="w-4 h-4" />
          </Button>
        </div>
      </section>

      {/* Mobile Fixed CTA */}
      <div className="fixed bottom-0 left-0 right-0 md:hidden bg-background/95 backdrop-blur-md border-t border-border/60 p-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] z-50">
        <Button size="lg" className="w-full rounded-full text-base gap-2" onClick={goToOrder}>
          주문하기 <ArrowRight className="w-4 h-4" />
        </Button>
      </div>

      <SiteFooter />
    </div>
  );
}
